import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PasswordPrompt } from './PasswordPrompt';

interface PasswordProtectedRouteProps {
  children: React.ReactNode;
  storageKey?: string;
}

/**
 * PasswordProtectedRoute - Simple password protection for routes
 *
 * Uses sessionStorage to persist access within a browser session.
 * Password is checked against REACT_APP_DEMO_PASSWORD env var (default: "CLEAR")
 *
 * To disable password protection, set REACT_APP_DEMO_PASSWORD_ENABLED=false
 *
 * Embedded mode (?embedded=true) bypasses password protection for landing page iframe integration.
 *
 * @param children - The protected content to render
 * @param storageKey - sessionStorage key for this protection scope (default: "clear_demo_unlocked")
 */
const PasswordProtectedRoute: React.FC<PasswordProtectedRouteProps> = ({
  children,
  storageKey = 'clear_demo_unlocked'
}) => {
  const [searchParams] = useSearchParams();
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Check if password protection is disabled via env var
  const isPasswordProtectionEnabled = process.env.REACT_APP_DEMO_PASSWORD_ENABLED !== 'false';

  // Allow embedded mode to bypass password protection (for landing page iframe)
  const isEmbeddedMode = searchParams.get('embedded') === 'true';

  useEffect(() => {
    // If password protection is disabled OR embedded mode, automatically unlock
    if (!isPasswordProtectionEnabled || isEmbeddedMode) {
      setIsUnlocked(true);
      setIsLoading(false);
      return;
    }

    // Check sessionStorage for existing unlock
    const unlocked = sessionStorage.getItem(storageKey);
    if (unlocked === 'true') {
      setIsUnlocked(true);
    }
    setIsLoading(false);
  }, [storageKey, isPasswordProtectionEnabled, isEmbeddedMode]);

  const handleUnlock = () => {
    sessionStorage.setItem(storageKey, 'true');
    setIsUnlocked(true);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-sand-50">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin border-4 border-solid border-accent-500 border-r-transparent mb-4"></div>
          <p className="text-ink-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isUnlocked) {
    return <PasswordPrompt onSuccess={handleUnlock} />;
  }

  return <>{children}</>;
};

export default PasswordProtectedRoute;
